import React from 'react';
import {StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';
import {fonts} from '@/shared/ui/fonts';
import {AppText} from '@/shared/ui/text';
import {radii, useTheme, withAlpha, type Palette} from '@/shared/ui/theme';

/**
 * The status system — и единственное, кроме акцента, место, где в интерфейсе есть
 * цвет.
 *
 * GREEN = live / online / granted, amber = pending / attention, red = denied. Offline
 * is muted grey, not red — an unreachable device is not an alarm; red is reserved for
 * a refused pass. The same tone means the same thing on a device, a camera, an access
 * event and a person, so one glance reads identically everywhere.
 *
 * ## Опасность — не акцент
 *
 * `denied` — коралловый `--danger`, не фирменный `#d71820`: отказ в таблице должен
 * отличаться от выбранной строки и от главной кнопки. Так устроены токены ui-kit, и
 * так красное на пульте остаётся сигналом, а тревога — тревогой.
 *
 * ## Метка — прямоугольный шильдик
 *
 * `Badge` ui-kit: тонкая рамка цветом метки, мягкая заливка того же тона, радиус 2pt,
 * 11px. «Таблетка» на плотном экране читается как кнопка, а нажимать её некуда.
 */
export type StatusTone =
  | 'online'
  | 'offline'
  | 'pending'
  | 'granted'
  | 'denied'
  | 'neutral';

type ToneStyle = {
  /** The dot, and any bar or rail. Bright; never used behind text. */
  fill: string;
  /** The label. Readable on `bg`; never used as a fill. */
  ink: string;
  /** The 12% wash behind a chip. */
  bg: string;
  /** A hairline in the tone, enough to define the chip's edge on any surface. */
  border: string;
};

export function toneStyles(p: Palette): Record<StatusTone, ToneStyle> {
  const live: ToneStyle = {
    fill: p.signal,
    ink: p.signalInk,
    bg: p.signalSoft,
    border: withAlpha(p.signal, 0.55),
  };
  const quiet: ToneStyle = {
    fill: withAlpha(p.mutedForeground, 0.7),
    ink: p.textSecondary,
    bg: p.neutralSoft,
    border: withAlpha(p.textSecondary, 0.45),
  };
  return {
    online: live,
    granted: live,
    pending: {
      fill: p.warning,
      ink: p.warningInk,
      bg: p.warningSoft,
      border: withAlpha(p.warning, 0.55),
    },
    denied: {
      fill: p.destructive,
      ink: p.destructiveInk,
      bg: p.destructiveSoft,
      border: withAlpha(p.destructive, 0.55),
    },
    offline: quiet,
    neutral: quiet,
  };
}

const styles = StyleSheet.create({
  /* The same geometry as `Badge`: the two sit side by side in a row all over this
     app. The DOT is what says "state" — the shape does not have to. */
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 5,
    minHeight: 20,
    borderWidth: 1,
    borderRadius: radii.xs,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  pillSm: {minHeight: 18, paddingHorizontal: 5, gap: 4},
  dot: {width: 8, height: 8, borderRadius: 4},
  /* Sentence case, never caps: these carry words like «Приостановлена», and a Cyrillic
     capital at chip scale has no silhouette to recognise it by. */
  label: {fontSize: 11, fontFamily: fonts.sans.regular, lineHeight: 15},
  labelSm: {fontSize: 11, fontFamily: fonts.sans.regular, lineHeight: 15},
});

/** A status dot. Static: at list scale a pulsing row is noise, not information. */
export function StatusDot({
  tone,
  size = 8,
  style,
}: {
  tone: StatusTone;
  size?: number;
  style?: StyleProp<ViewStyle>;
}) {
  const t = toneStyles(useTheme())[tone];
  return (
    <View
      testID="status-dot"
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[
        styles.dot,
        {width: size, height: size, borderRadius: size / 2, backgroundColor: t.fill},
        style,
      ]}
    />
  );
}

/**
 * A dot with a lit halo around it — for the one place on a screen where the live state
 * IS the headline (a device card's header, the context chip). At list scale use the
 * bare dot; a row of halos is glitter.
 *
 * The halo sits OUTSIDE the mark, at 20% of its own colour. Inset, it swallowed the 8pt
 * dot whole and left a pale ring where the state used to be.
 */
export function StatusBeacon({tone, size = 8}: {tone: StatusTone; size?: number}) {
  const t = toneStyles(useTheme())[tone];
  const outer = size * 2.25;
  return (
    <View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={{
        width: outer,
        height: outer,
        borderRadius: outer / 2,
        backgroundColor: withAlpha(t.fill, 0.2),
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <View
        style={{width: size, height: size, borderRadius: size / 2, backgroundColor: t.fill}}
      />
    </View>
  );
}

/** Quiet status chip: dot + label on a 12% wash of its own tone. */
export function StatusPill({
  tone,
  label,
  size = 'md',
  /** Hide the dot when the label alone carries the state (e.g. a flag pill). */
  dot = true,
  style,
}: {
  tone: StatusTone;
  label: string;
  size?: 'sm' | 'md';
  dot?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const t = toneStyles(useTheme())[tone];
  const small = size === 'sm';
  return (
    <View
      testID="status-pill"
      accessibilityRole="text"
      accessibilityLabel={label}
      style={[
        styles.pill,
        small && styles.pillSm,
        {backgroundColor: t.bg, borderColor: t.border},
        style,
      ]}>
      {dot ? <StatusDot tone={tone} size={6} /> : null}
      <AppText color={t.ink} style={small ? styles.labelSm : styles.label}>
        {label}
      </AppText>
    </View>
  );
}

/** Map an online flag to a tone. */
export function onlineTone(online: boolean): StatusTone {
  return online ? 'online' : 'offline';
}
